import React, {
  ComponentProps,
  type ComponentPropsWithoutRef,
  type ElementType,
  forwardRef,
  type ReactElement,
  Ref,
} from 'react';
import { IconButton as MuiIconButton, touchRippleClasses } from '@mui/material';

type HTMLTagMap = {
  div: HTMLDivElement;
  span: HTMLSpanElement;
  a: HTMLAnchorElement;
  button: HTMLButtonElement;
};

type MuiProps = ComponentProps<typeof MuiIconButton>;

type PolymorphicRef<C extends ElementType> = C extends keyof HTMLTagMap ? Ref<HTMLTagMap[C]> : Ref<unknown>;

export type Props<C extends ElementType> = {
  component?: C;
  readOnly?: boolean;
} & MuiProps &
  Omit<ComponentPropsWithoutRef<C>, keyof MuiProps | 'component'>;

function PolymorphicComponentImpl<C extends ElementType = 'button'>(
  props: Props<C>,
  ref: PolymorphicRef<C>,
): ReactElement {
  const { readOnly, sx, ...rest } = props;

  return (
    <MuiIconButton
      {...rest}
      // TypeScript can't infer polymorphic ref on MUI base, safe to cast
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as Ref<any>}
      sx={{
        ...(readOnly && {
          '&:hover': {
            backgroundColor: 'transparent',
            cursor: 'default',
          },
          [`& .${touchRippleClasses.root}`]: {
            opacity: 0,
            visibility: 'hidden',
          },
        }),
        ...sx,
      }}
    />
  );
}

const PolymorphicComponent = forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  PolymorphicComponentImpl as unknown as (props: Props<any>, ref: PolymorphicRef<any>) => ReactElement,
);

const IconButton = PolymorphicComponent as <C extends ElementType = 'button'>(
  props: Props<C> & { ref?: PolymorphicRef<C> },
) => ReactElement | null;

// @ts-expect-error TS can't detect displayName from forwardRef
IconButton.displayName = 'IconButton';

export default IconButton;
